import { useMemo, SyntheticEvent, useRef } from "react";
import FamilyRestroomIcon from "@mui/icons-material/FamilyRestroom";
import WorkIcon from "@mui/icons-material/Work";
import GpsNotFixedIcon from "@mui/icons-material/GpsNotFixed";
import AssignmentIcon from "@mui/icons-material/Assignment";
import {
  FilterList,
  FilterListItem,
  FilterLiveSearch,
  SavedQueriesList,
  FilterLiveForm,
  useGetList,
} from "react-admin";
import { Autocomplete, Stack, TextField } from "@mui/material";
import { Controller, useFormContext } from "react-hook-form";
import { StyledListAside } from "@/components/ui/react-admin/StyledListAside";
import { ResourceName, type Claimant, type ValidAny } from "@/types";
import { formatRelationship } from "@/utils/formatters/stringFormatters";

// ─── Hook: extract unique filter values from all claimants ──────────────────

const useClaimantFilterOptions = () => {
  const { data: claimants } = useGetList<Claimant>(ResourceName.CLAIMANTS, {
    pagination: { page: 1, perPage: 10000 },
    sort: { field: "claimant_name", order: "ASC" },
  });

  return useMemo(() => {
    if (!claimants) return { relationships: [], clientIds: new Set<string>() };

    const relationships = [...new Set(claimants.map((c) => c.claimant_relationship).filter(Boolean))] as string[];

    const clientIds = new Set(
      claimants
        .map((c) => c.client_id_ref)
        .filter(Boolean)
        .map(String)
    );

    return { relationships, clientIds };
  }, [claimants]);
};

// ─── Autocomplete filter input ──────────────────────────────────────────────

const ClientFilterInput = ({ clientIds }: { clientIds: Set<string> }) => {
  const { control } = useFormContext();
  const hasInitialized = useRef(false);

  const { data: allClients, isLoading } = useGetList(ResourceName.CLIENTS, {
    pagination: { page: 1, perPage: 1000 },
    sort: { field: "client_legal_name", order: "ASC" },
  });

  // Only show clients that have claimants
  const options = useMemo(() => (allClients || []).filter((c) => clientIds.has(String(c.id))), [allClients, clientIds]);

  if (!isLoading && options.length > 0) {
    hasInitialized.current = true;
  }

  return (
    <Controller
      name="client_id_ref"
      control={control}
      render={({ field }) => (
        <Autocomplete
          options={options}
          getOptionLabel={(option) => option?.client_legal_name || ""}
          value={hasInitialized.current ? (options.find((c) => c.id === field.value) ?? null) : null}
          onChange={(_event: SyntheticEvent, newValue: ValidAny) => {
            field.onChange(newValue?.id || null);
          }}
          isOptionEqualToValue={(option, value) => option?.id === value?.id}
          loading={isLoading}
          size="small"
          renderInput={(params) => <TextField {...params} label="Client" variant="outlined" />}
          fullWidth
          clearOnEscape
        />
      )}
    />
  );
};

// ─── Main aside ─────────────────────────────────────────────────────────────

export const ClaimantListAside = () => {
  const { relationships, clientIds } = useClaimantFilterOptions();

  return (
    <StyledListAside>
      <FilterLiveSearch />
      <SavedQueriesList />

      <FilterLiveForm>
        <Stack spacing={2}>
          <ClientFilterInput clientIds={clientIds} />
        </Stack>
      </FilterLiveForm>

      {/* Relationship Filter — data-aware, only shows values present in list */}
      {relationships.length > 0 && (
        <FilterList label="Relationship" icon={<FamilyRestroomIcon />}>
          {relationships.map((rel) => (
            <FilterListItem key={rel} label={formatRelationship(rel)} value={{ claimant_relationship: rel }} />
          ))}
        </FilterList>
      )}

      {/* Work Status Filter — searches across all policy_data entries */}
      <FilterList label="Work Status" icon={<WorkIcon />}>
        <FilterListItem label="Actively Working" value={{ is_actively_working: true }} />
        <FilterListItem label="Not Actively Working" value={{ is_actively_working: false }} />
      </FilterList>

      {/* Lasered Filter */}
      <FilterList label="Lasered" icon={<GpsNotFixedIcon />}>
        <FilterListItem label="Is Lasered" value={{ is_lasered: true }} />
      </FilterList>

      {/* Tasks Filter */}
      <FilterList label="Tasks" icon={<AssignmentIcon />}>
        <FilterListItem label="Has Open Tasks" value={{ has_open_tasks: "true" }} />
      </FilterList>
    </StyledListAside>
  );
};
